import React, { useCallback, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import FilterListIcon from "@mui/icons-material/FilterList";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import RequestQuoteOutlinedIcon from "@mui/icons-material/RequestQuoteOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import DownloadIcon from "@mui/icons-material/Download";
import moment from "moment";
import ResumenSeleccion from "./ResumenSeleccion";
import {
  exportarPendientesEmpresaSeleccionados,
  exportarPendientesEmpresaTodo,
} from "./ApiServices";
import {
  CLAVE_PAGOS_NO_PROVEEDOR,
  clampImporte,
  configPagoProveedorVacia,
  esDocumentoAdelanto,
  esGrupoPagosNoProveedor,
  esPagoNoProveedor,
  formatCuit,
  formatMoneda,
  guardarBlobComoArchivo,
  etiquetaComprobanteCorta,
  idDocumentoPendiente,
  importeEsValido,
  limitesImporteInput,
  motivoNoSeleccionableLabel,
  tooltipTipoComprobante,
} from "./utils";

const cuitProveedor = (prov) =>
  String(prov?.nro_cuit || prov?.cuit || "").replace(/\D/g, "");

const claveGrupoProveedor = (prov) =>
  esGrupoPagosNoProveedor(prov) ? CLAVE_PAGOS_NO_PROVEEDOR : cuitProveedor(prov);

const docPerteneceAGrupo = (doc, clave) => {
  if (clave === CLAVE_PAGOS_NO_PROVEEDOR) return esPagoNoProveedor(doc);
  return cuitProveedor(doc) === clave;
};

/** Sin CBU no bloquea el pago: se puede pagar con cheque u otros medios. */
const motivoPermiteSeleccionSinCbu = (motivo) =>
  String(motivo || "").toLowerCase() === "sin_cbu";

const proveedorBloqueaSeleccion = (prov) => {
  if (prov?.seleccionable !== false) return false;
  if (motivoPermiteSeleccionSinCbu(prov?.motivo_no_seleccionable)) return false;
  const sinCbu = !String(prov?.cbu || "").replace(/\s/g, "");
  const sinBanco =
    (prov?.id_banco == null || String(prov.id_banco).trim() === "") &&
    !String(prov?.banco_nombre || "").trim();
  if (sinCbu || sinBanco) return false;
  return true;
};

/** Adelantos son seleccionables si el proveedor no está bloqueado por otro motivo. */
const docEsSeleccionable = (prov, doc) => {
  if (proveedorBloqueaSeleccion(prov)) return false;
  if (esDocumentoAdelanto(doc)) return true;
  if (doc?.seleccionable) return true;
  const motivo =
    doc?.motivo_no_seleccionable ||
    doc?.motivo_no_seleccionable_proveedor ||
    prov?.motivo_no_seleccionable;
  if (motivoPermiteSeleccionSinCbu(motivo)) return true;
  const sinCbu = !String(prov?.cbu || "").replace(/\s/g, "");
  return sinCbu;
};

const formatearFechaFiltro = (value) => {
  if (!value) return null;
  const m = moment(value, ["YYYY-MM-DD", moment.ISO_8601], true);
  return m.isValid() ? m.format("DD/MM/YYYY") : String(value);
};

const etiquetaRangoVencimiento = (filtros = {}) => {
  const desde = formatearFechaFiltro(filtros.vto_desde);
  const hasta = formatearFechaFiltro(filtros.vto_hasta);
  if (desde && hasta) {
    return `Fecha vencimiento desde ${desde} — Fecha vencimiento hasta ${hasta}`;
  }
  if (desde) return `Fecha vencimiento desde: ${desde}`;
  if (hasta) return `Fecha vencimiento hasta: ${hasta}`;
  return null;
};

const thSx = {
  bgcolor: "#2471A3",
  color: "#fff",
  fontWeight: 600,
  whiteSpace: "nowrap",
  py: 0.75,
  px: 1,
  fontSize: 14,
};

const FiltrosPendientes = ({
  filtros,
  onChange,
  onAplicar,
  onLimpiar,
  imputaciones = [],
  loading,
}) => (
  <Paper
    component="form"
    onSubmit={(e) => {
      e.preventDefault();
      onAplicar?.();
    }}
    elevation={1}
    sx={{ p: 2, mb: 2, backgroundColor: "#fff", border: "1px solid #e0e0e0" }}
  >
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
      <FilterListIcon color="action" fontSize="small" />
      <Typography variant="subtitle2" fontWeight={600}>
        Filtros
      </Typography>
    </Stack>
    <Grid container spacing={1.5} alignItems="center">
      <Grid item xs={12} sm={6} md={3}>
        <TextField
          fullWidth
          size="small"
          type="date"
          label="Fecha vencimiento desde"
          InputLabelProps={{ shrink: true }}
          value={filtros.vto_desde || ""}
          onChange={(e) => onChange({ vto_desde: e.target.value })}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <TextField
          fullWidth
          size="small"
          type="date"
          label="Fecha vencimiento hasta"
          InputLabelProps={{ shrink: true }}
          value={filtros.vto_hasta || ""}
          onChange={(e) => onChange({ vto_hasta: e.target.value })}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={2}>
        <TextField
          fullWidth
          size="small"
          label="CUIT proveedor"
          placeholder="Opcional"
          value={filtros.cuit || ""}
          onChange={(e) => onChange({ cuit: e.target.value })}
          inputProps={{ inputMode: "numeric" }}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={2}>
        <FormControl fullWidth size="small">
          <InputLabel>Imputación</InputLabel>
          <Select
            label="Imputación"
            value={
              filtros.id_imputacion === "" || filtros.id_imputacion == null
                ? ""
                : String(filtros.id_imputacion)
            }
            onChange={(e) => {
              const v = e.target.value;
              onChange({ id_imputacion: v === "" ? "" : Number(v) });
            }}
          >
            <MenuItem value="">Todas</MenuItem>
            {imputaciones.map((imp) => (
              <MenuItem key={imp.idimputacion} value={String(imp.idimputacion)}>
                {imp.descripcion || `Imputación ${imp.idimputacion}`}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} md={2}>
        <Stack direction="row" spacing={1}>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            sx={{ minWidth: 90 }}
          >
            Aplicar
          </Button>
          <Button
            type="button"
            variant="outlined"
            disabled={loading}
            onClick={onLimpiar}
          >
            Limpiar
          </Button>
        </Stack>
      </Grid>
    </Grid>
  </Paper>
);

const FilaDocumento = ({
  doc,
  checked,
  disabled,
  importe,
  onToggle,
  onImporteChange,
  onImporteBlur,
}) => {
  const saldo = Number(doc.saldo_pendiente ?? doc.monto);
  const invalido = checked && !importeEsValido(importe, saldo, doc);
  const limites = limitesImporteInput(saldo, doc);
  const motivo =
    doc.motivo_no_seleccionable || doc.motivo_no_seleccionable_proveedor;
  const esAdelanto = esDocumentoAdelanto(doc);
  const esPnp = esPagoNoProveedor(doc);
  const saldoNegativo = Number.isFinite(saldo) && saldo < 0;
  const etiquetaComprobante = etiquetaComprobanteCorta(doc);
  const tooltipComprobante = tooltipTipoComprobante(doc) || etiquetaComprobante;

  return (
    <TableRow
      hover={!disabled}
      selected={checked}
      sx={{
        opacity: disabled ? 0.55 : 1,
        bgcolor: esPnp
          ? "rgba(123, 31, 162, 0.06)"
          : esAdelanto
            ? "rgba(2, 136, 209, 0.06)"
            : checked
              ? "rgba(36, 113, 163, 0.08)"
              : "#fff",
        "& td": { py: 0.5, fontSize: 14, px: 0.75 },
      }}
    >
      <TableCell
        padding="none"
        align="center"
        sx={{ width: 44, minWidth: 44, maxWidth: 44 }}
      >
        <Tooltip
          title={
            disabled
              ? motivoNoSeleccionableLabel(motivo) || "No seleccionable"
              : ""
          }
        >
          <span>
            <Checkbox
              size="small"
              checked={checked}
              disabled={disabled}
              onChange={onToggle}
            />
          </span>
        </Tooltip>
      </TableCell>
      <TableCell sx={{ width: 88, minWidth: 88, whiteSpace: "nowrap" }}>
        {doc.fecha_factura || doc.fecha
          ? moment(doc.fecha_factura || doc.fecha).format("DD/MM/YYYY")
          : "—"}
      </TableCell>
      <TableCell sx={{ width: 168, minWidth: 152, maxWidth: 180 }}>
        <Stack spacing={0.25} sx={{ minWidth: 0 }}>
          <Tooltip title={tooltipComprobante}>
            <Typography
              variant="body2"
              noWrap
              sx={{
                fontSize: 12,
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {etiquetaComprobante}
            </Typography>
          </Tooltip>
          {esAdelanto ? (
            <Chip size="small" color="info" label="Adelanto" sx={{ height: 20, alignSelf: "flex-start" }} />
          ) : null}
        </Stack>
      </TableCell>
      <TableCell sx={{ width: 88, minWidth: 88, whiteSpace: "nowrap" }}>
        {doc.fecha_vencimiento
          ? moment(doc.fecha_vencimiento).format("DD/MM/YYYY")
          : "—"}
      </TableCell>
      <TableCell align="right" sx={{ width: 188, minWidth: 176, whiteSpace: "nowrap" }}>
        {formatMoneda(
          doc.total ?? doc.monto ?? (esAdelanto || esPnp ? Math.abs(saldo) : null)
        )}
      </TableCell>
      <TableCell
        align="right"
        sx={{
          width: 188,
          minWidth: 176,
          whiteSpace: "nowrap",
          color: saldoNegativo ? "success.main" : undefined,
          fontWeight: saldoNegativo ? 600 : undefined,
        }}
      >
        {formatMoneda(doc.saldo_pendiente ?? doc.monto)}
      </TableCell>
      <TableCell align="right" sx={{ width: 196, minWidth: 184 }}>
        {checked && !esPnp ? (
          <TextField
            size="small"
            type="number"
            value={importe ?? ""}
            error={invalido}
            onClick={(e) => e.stopPropagation()}
            onChange={onImporteChange}
            onBlur={onImporteBlur}
            inputProps={limites}
            sx={{
              width: "100%",
              "& .MuiInputBase-root": { height: 28, fontSize: 12 },
              "& .MuiInputBase-input": { py: 0, px: 0.75 },
            }}
          />
        ) : (
          <Typography
            variant="body2"
            color={checked ? "text.primary" : "text.secondary"}
            noWrap
            sx={{ fontSize: 12, fontWeight: esPnp ? 600 : undefined }}
          >
            {formatMoneda(
              esPnp
                ? doc.saldo_pendiente ?? doc.monto
                : importe ?? doc.saldo_pendiente ?? doc.monto
            )}
          </Typography>
        )}
      </TableCell>
    </TableRow>
  );
};

const FilaProveedor = ({
  proveedor,
  documentos,
  rowSelection,
  importesAPagar,
  onToggleDoc,
  onToggleTodosProveedor,
  onImportesAPagarChange,
  open,
  onToggleOpen,
}) => {
  const esPagosVarios = esGrupoPagosNoProveedor(proveedor);
  const clave = claveGrupoProveedor(proveedor);
  const cuit = cuitProveedor(proveedor);
  const docsProv = documentos.filter((d) => docPerteneceAGrupo(d, clave));
  const seleccionables = docsProv.filter((d) => d.puede_seleccionar);
  const idsSeleccionados = seleccionables.filter(
    (d) => rowSelection[idDocumentoPendiente(d)]
  );
  const todosMarcados =
    seleccionables.length > 0 &&
    idsSeleccionados.length === seleccionables.length;
  const algunoMarcado = idsSeleccionados.length > 0;
  const proveedorBloqueado = proveedorBloqueaSeleccion(proveedor);

  return (
    <>
      <TableRow
        hover
        onClick={onToggleOpen}
        sx={{
          cursor: "pointer",
          bgcolor: algunoMarcado
            ? esPagosVarios
              ? "rgba(123, 31, 162, 0.1)"
              : "rgba(36, 113, 163, 0.08)"
            : open
              ? esPagosVarios
                ? "rgba(123, 31, 162, 0.04)"
                : "rgba(36, 113, 163, 0.04)"
              : "#fafafa",
          "& > *": { borderBottom: open ? "unset" : undefined },
          "& td": { py: 0.75, px: 0.75, fontSize: 12 },
        }}
      >
        <TableCell
          padding="none"
          align="center"
          sx={{ width: 44, minWidth: 44, maxWidth: 44 }}
          onClick={(e) => e.stopPropagation()}
        >
          <Checkbox
            size="small"
            checked={todosMarcados}
            indeterminate={algunoMarcado && !todosMarcados}
            disabled={proveedorBloqueado || seleccionables.length === 0}
            onChange={(e) => onToggleTodosProveedor(clave, e.target.checked)}
          />
        </TableCell>
        <TableCell sx={{ width: 40, minWidth: 40 }} padding="none">
          <IconButton size="small" aria-label={open ? "Cerrar" : "Abrir"}>
            {open ? (
              <KeyboardArrowUpIcon fontSize="small" />
            ) : (
              <KeyboardArrowDownIcon fontSize="small" />
            )}
          </IconButton>
        </TableCell>
        <TableCell sx={{ minWidth: 140 }}>
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
            <Typography variant="body2" fontWeight={700} noWrap sx={{ fontSize: 12 }}>
              {proveedor.razon_social || "Sin razón social"}
            </Typography>
            {esPagosVarios ? (
              <Chip size="small" color="secondary" label="Pagos varios" />
            ) : null}
          </Stack>
        </TableCell>
        <TableCell sx={{ width: 110, minWidth: 100, whiteSpace: "nowrap" }}>
          <Typography variant="body2" noWrap sx={{ fontSize: 12 }}>
            {esPagosVarios ? "—" : formatCuit(cuit)}
          </Typography>
        </TableCell>
        <TableCell sx={{ width: 90, minWidth: 80, maxWidth: 100 }}>
          <Typography variant="body2" color="text.secondary" noWrap sx={{ fontSize: 12 }}>
            {esPagosVarios ? "—" : proveedor.banco_nombre || "—"}
          </Typography>
        </TableCell>
        <TableCell sx={{ width: 110, minWidth: 90, maxWidth: 110 }}>
          <Typography
            variant="body2"
            color="text.secondary"
            noWrap
            title={proveedor.cbu || ""}
            sx={{ maxWidth: 100, fontSize: 12 }}
          >
            {esPagosVarios ? "—" : proveedor.cbu || "—"}
          </Typography>
        </TableCell>
        <TableCell align="center" sx={{ width: 52, minWidth: 48 }}>
          <Chip
            size="small"
            variant="outlined"
            label={proveedor.resumen?.cantidad ?? docsProv.length}
          />
        </TableCell>
        <TableCell align="right" sx={{ width: 110, minWidth: 100, whiteSpace: "nowrap" }}>
          <Typography variant="body2" fontWeight={700} color="primary.main" sx={{ fontSize: 12 }}>
            {formatMoneda(proveedor.resumen?.saldo_total)}
          </Typography>
        </TableCell>
     
      </TableRow>

      <TableRow>
        <TableCell
          colSpan={8}
          sx={{
            py: 0,
            px: 0,
            borderBottom: open ? "1px solid #e0e0e0" : "none",
            bgcolor: "#f5f7fa",
          }}
        >
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ px: 1, py: 1, width: "100%" }}>
              <TableContainer
                component={Paper}
                variant="outlined"
                sx={{
                  width: "100%",
                  maxHeight: 360,
                  overflowX: "auto",
                  overflowY: "auto",
                }}
              >
                <Table
                  size="small"
                  stickyHeader
                  sx={{ width: "100%", minWidth: 960, tableLayout: "fixed" }}
                >
                  <colgroup>
                    <col style={{ width: 44 }} />
                    <col style={{ width: 88 }} />
                    <col style={{ width: 168 }} />
                    <col style={{ width: 88 }} />
                    <col />
                    <col />
                    <col />
                  </colgroup>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        padding="none"
                        sx={{ ...thSx, width: 44, minWidth: 44, maxWidth: 44 }}
                      />
                      <TableCell sx={{ ...thSx, width: 88, minWidth: 88 }}>
                        Fecha
                      </TableCell>
                      <TableCell sx={{ ...thSx, width: 168, minWidth: 152, maxWidth: 180 }}>
                        Comprobante
                      </TableCell>
                      <TableCell sx={{ ...thSx, width: 88, minWidth: 88 }}>
                        Vto.
                      </TableCell>
                      <TableCell align="right" sx={{ ...thSx, width: 188, minWidth: 176 }}>
                        Total
                      </TableCell>
                      <TableCell align="right" sx={{ ...thSx, width: 188, minWidth: 176 }}>
                        Saldo
                      </TableCell>
                      <TableCell align="right" sx={{ ...thSx, width: 196, minWidth: 184 }}>
                        A pagar
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {docsProv.map((doc) => {
                      const id = idDocumentoPendiente(doc);
                      if (!id) return null;
                      const checked = Boolean(rowSelection[id]);
                      const disabled = !doc.puede_seleccionar;

                      return (
                        <FilaDocumento
                          key={id}
                          doc={doc}
                          checked={checked}
                          disabled={disabled}
                          importe={
                            importesAPagar[id] ??
                            doc.saldo_pendiente ??
                            doc.monto
                          }
                          onToggle={() => onToggleDoc(doc, !checked)}
                          onImporteChange={(e) =>
                            onImportesAPagarChange((prev) => ({
                              ...prev,
                              [id]: e.target.value,
                            }))
                          }
                          onImporteBlur={() =>
                            onImportesAPagarChange((prev) => {
                              const saldo =
                                doc.saldo_pendiente ?? doc.monto;
                              return {
                                ...prev,
                                [id]: clampImporte(
                                  prev[id] ?? saldo,
                                  saldo,
                                  doc
                                ),
                              };
                            })
                          }
                        />
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const ListadoPendientesEmpresa = ({
  filtros,
  onFiltrosChange,
  onAplicarFiltros,
  onLimpiarFiltros,
  filtrosAplicados = null,
  idEmpresa = null,
  imputaciones = [],
  proveedores = [],
  resumen = null,
  loading = false,
  error = "",
  rowSelection,
  onRowSelectionChange,
  importesAPagar,
  onImportesAPagarChange,
  preview,
  previewLoading,
  previewError,
  filasSeleccionadas,
  puedeContinuar,
  onContinuar,
  labelContinuar = "Siguiente",
  onAbrirAdelanto,
  modoEdicion = false,
  configPagoPorProveedor = {},
  onConfigPagoPorProveedorChange,
  cuentasBancarias = [],
  loadingCuentas = false,
}) => {
  const [expandidos, setExpandidos] = useState(() => {
    if (!modoEdicion) return {};
    const initial = {};
    (proveedores || []).forEach((prov) => {
      const key = String(
        esGrupoPagosNoProveedor(prov)
          ? CLAVE_PAGOS_NO_PROVEEDOR
          : prov.id_proveedor ?? cuitProveedor(prov)
      );
      if (key) initial[key] = true;
    });
    return initial;
  });
  const [exportando, setExportando] = useState(false);
  const rangoVto = etiquetaRangoVencimiento(filtrosAplicados || filtros);
  const mostrarConfigPago = !modoEdicion;

  const handleConfigPagoChange = useCallback(
    (cuit, next) => {
      if (!onConfigPagoPorProveedorChange) return;
      onConfigPagoPorProveedorChange((prev) => ({
        ...prev,
        [String(cuit)]: {
          ...(prev?.[String(cuit)] || configPagoProveedorVacia()),
          ...next,
        },
      }));
    },
    [onConfigPagoPorProveedorChange]
  );

  const proveedoresSeleccionadosConfig = useMemo(() => {
    const map = new Map();
    (filasSeleccionadas || []).forEach((doc) => {
      if (esPagoNoProveedor(doc)) return;
      const cuit = cuitProveedor(doc);
      if (!cuit || map.has(cuit)) return;
      map.set(cuit, {
        cuit,
        razon_social: doc.razon_social || "",
        cbu: doc.cbu || "",
        id_banco: doc.id_banco ?? null,
        banco_nombre: doc.banco_nombre || "",
      });
    });
    return Array.from(map.values());
  }, [filasSeleccionadas]);

  const documentos = useMemo(
    () =>
      proveedores.flatMap((prov) =>
        (prov.documentos ?? []).map((doc) => ({
          ...doc,
          id_proveedor: prov.id_proveedor,
          razon_social: prov.razon_social,
          nro_cuit: prov.nro_cuit,
          cbu: prov.cbu,
          id_banco: prov.id_banco,
          banco_nombre: prov.banco_nombre,
          seleccionable_proveedor: prov.seleccionable,
          motivo_no_seleccionable_proveedor: prov.motivo_no_seleccionable,
          puede_seleccionar: docEsSeleccionable(prov, doc),
          es_pagos_no_proveedor: Boolean(prov.es_pagos_no_proveedor),
          es_pago_no_proveedor:
            Boolean(doc.es_pago_no_proveedor) ||
            Boolean(prov.es_pagos_no_proveedor) ||
            doc.id_pago_no_proveedor != null,
          importe_a_pagar:
            doc.importe_a_pagar != null
              ? doc.importe_a_pagar
              : doc.saldo_pendiente ?? doc.monto,
        }))
      ),
    [proveedores]
  );

  const filtrosExport = useMemo(() => {
    const base = filtrosAplicados || filtros || {};
    return {
      id_empresa: idEmpresa ?? base.id_empresa,
      vto_desde: base.vto_desde || undefined,
      vto_hasta: base.vto_hasta || undefined,
      cuit: base.cuit || undefined,
      id_imputacion:
        base.id_imputacion !== "" && base.id_imputacion != null
          ? base.id_imputacion
          : undefined,
    };
  }, [filtrosAplicados, filtros, idEmpresa]);

  const seleccionExport = useMemo(() => {
    const ids = [];
    const cuits = [];
    (proveedores || []).forEach((prov) => {
      if (esGrupoPagosNoProveedor(prov)) return;
      const docs = documentos.filter(
        (d) => cuitProveedor(d) === cuitProveedor(prov)
      );
      const tieneSeleccion = docs.some(
        (d) => rowSelection?.[idDocumentoPendiente(d)]
      );
      if (!tieneSeleccion) return;
      const idProv = Number(prov.id_proveedor);
      if (Number.isFinite(idProv) && idProv > 0) ids.push(idProv);
      const cuit = cuitProveedor(prov);
      if (cuit) cuits.push(cuit);
    });
    return { ids, cuits };
  }, [proveedores, documentos, rowSelection]);

  const handleExportarTodo = useCallback(async () => {
    if (filtrosExport.id_empresa == null || filtrosExport.id_empresa === "") {
      window.alert("Seleccione una empresa para exportar");
      return;
    }
    setExportando(true);
    try {
      const { blob, filename } = await exportarPendientesEmpresaTodo(
        filtrosExport
      );
      guardarBlobComoArchivo(blob, filename);
    } catch (err) {
      window.alert(err.message || "Error al exportar pendientes");
    } finally {
      setExportando(false);
    }
  }, [filtrosExport]);

  const handleExportarSeleccionados = useCallback(async () => {
    if (filtrosExport.id_empresa == null || filtrosExport.id_empresa === "") {
      window.alert("Seleccione una empresa para exportar");
      return;
    }
    if (!seleccionExport.ids.length && !seleccionExport.cuits.length) {
      window.alert("Seleccione al menos un proveedor para exportar");
      return;
    }
    setExportando(true);
    try {
      const { blob, filename } = await exportarPendientesEmpresaSeleccionados(
        filtrosExport,
        seleccionExport
      );
      guardarBlobComoArchivo(blob, filename);
    } catch (err) {
      window.alert(err.message || "Error al exportar seleccionados");
    } finally {
      setExportando(false);
    }
  }, [filtrosExport, seleccionExport]);

  const handleToggleDoc = useCallback(
    (doc, marcar) => {
      if (!doc.puede_seleccionar && marcar) return;
      const id = idDocumentoPendiente(doc);
      if (!id) return;
      onRowSelectionChange((prev) => {
        const next = { ...prev };
        if (marcar) next[id] = true;
        else delete next[id];
        return next;
      });
    },
    [onRowSelectionChange]
  );

  const handleToggleTodosProveedor = useCallback(
    (clave, marcar) => {
      onRowSelectionChange((prev) => {
        const next = { ...prev };
        documentos
          .filter((d) => docPerteneceAGrupo(d, clave) && d.puede_seleccionar)
          .forEach((d) => {
            const id = idDocumentoPendiente(d);
            if (!id) return;
            if (marcar) next[id] = true;
            else delete next[id];
          });
        return next;
      });
    },
    [documentos, onRowSelectionChange]
  );

  return (
    <Box>
      {!modoEdicion ? (
        <FiltrosPendientes
          filtros={filtros}
          onChange={(partial) =>
            onFiltrosChange?.((prev) => ({ ...prev, ...partial }))
          }
          onAplicar={onAplicarFiltros}
          onLimpiar={onLimpiarFiltros}
          imputaciones={imputaciones}
          loading={loading}
        />
      ) : null}

      {!loading && !modoEdicion && (resumen || rangoVto) && (
        <Paper
          elevation={0}
          sx={{ p: 1.5, mb: 2, bgcolor: "#fff", border: "1px solid #e8e8e8" }}
        >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.5}
              alignItems={{ xs: "flex-start", sm: "center" }}
              justifyContent="space-between"
            >
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                alignItems={{ xs: "flex-start", sm: "center" }}
                flexWrap="wrap"
              >
                {resumen ? (
                  <Typography variant="body2" color="text.secondary">
                    <strong>
                      {resumen.cantidad_proveedores ?? proveedores.length}
                    </strong>{" "}
                    proveedores ·{" "}
                    <strong>
                      {resumen.cantidad_documentos ?? documentos.length}
                    </strong>{" "}
                    comprobantes
                  </Typography>
                ) : null}
                {rangoVto ? (
                  <Chip
                    size="small"
                    color="primary"
                    variant="outlined"
                    label={rangoVto}
                    sx={{ fontWeight: 600 }}
                  />
                ) : (
                  <Chip
                    size="small"
                    variant="outlined"
                    label="Sin filtro de vencimiento"
                    sx={{ color: "text.secondary" }}
                  />
                )}
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={handleExportarTodo}
                  disabled={exportando || loading || !proveedores.length}
                >
                  Exportar todo
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={handleExportarSeleccionados}
                  disabled={
                    exportando ||
                    loading ||
                    (!seleccionExport.ids.length && !seleccionExport.cuits.length)
                  }
                >
                  Exportar seleccionados
                  {seleccionExport.cuits.length
                    ? ` (${seleccionExport.cuits.length})`
                    : ""}
                </Button>
                {resumen ? (
                  <Typography
                    variant="body2"
                    fontWeight={700}
                    color="primary.main"
                  >
                    Saldo total: {formatMoneda(resumen.saldo_total)}
                  </Typography>
                ) : null}
              </Stack>
            </Stack>
        </Paper>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && !error && proveedores.length === 0 && (
        <Alert severity="info">
          {modoEdicion
            ? "No quedan comprobantes en esta orden."
            : "No hay comprobantes pendientes para los filtros indicados."}
        </Alert>
      )}

      {!loading && proveedores.length > 0 && (
        <Grid container spacing={2} alignItems="flex-start">
          <Grid item xs={12} lg={modoEdicion ? 12 : 8}>
            {!modoEdicion ? (
              <Alert severity="info" sx={{ mb: 1.5 }}>
                Podés seleccionar comprobantes de varios proveedores. Se generará
                una orden de pago por cada uno. Expandí la fila para ver y marcar
                los documentos.
              </Alert>
            ) : null}
            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{
                backgroundColor: "#fff",
                overflowX: "auto",
                width: "100%",
              }}
            >
              <Table
                size="small"
                sx={{ width: "100%", minWidth: 760, tableLayout: "fixed" }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell
                      padding="none"
                      sx={{ ...thSx, width: 44, minWidth: 44, maxWidth: 44 }}
                    />
                    <TableCell sx={{ ...thSx, width: 40, minWidth: 40 }} />
                    <TableCell sx={{ ...thSx, minWidth: 140 }}>Proveedor</TableCell>
                    <TableCell sx={{ ...thSx, width: 110, minWidth: 100 }}>
                      CUIT
                    </TableCell>
                    <TableCell sx={{ ...thSx, width: 90, minWidth: 80 }}>
                      Banco
                    </TableCell>
                    <TableCell sx={{ ...thSx, width: 110, minWidth: 90 }}>
                      CBU
                    </TableCell>
                    <TableCell align="center" sx={{ ...thSx, width: 52, minWidth: 48 }}>
                      Docs
                    </TableCell>
                    <TableCell align="right" sx={{ ...thSx, width: 110, minWidth: 100 }}>
                      Saldo
                    </TableCell>
                   
                  </TableRow>
                </TableHead>
                <TableBody>
                  {proveedores.map((prov) => {
                    const clave = claveGrupoProveedor(prov);
                    const key = String(
                      esGrupoPagosNoProveedor(prov)
                        ? CLAVE_PAGOS_NO_PROVEEDOR
                        : prov.id_proveedor ?? clave
                    );
                    return (
                      <FilaProveedor
                        key={key}
                        proveedor={prov}
                        documentos={documentos}
                        rowSelection={rowSelection}
                        importesAPagar={importesAPagar}
                        onToggleDoc={handleToggleDoc}
                        onToggleTodosProveedor={handleToggleTodosProveedor}
                        onImportesAPagarChange={onImportesAPagarChange}
                        open={Boolean(expandidos[key])}
                        onToggleOpen={() =>
                          setExpandidos((prev) => ({
                            ...prev,
                            [key]: !prev[key],
                          }))
                        }
                      />
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          {!modoEdicion ? (
            <Grid
              item
              xs={12}
              lg={4}
              sx={{
                position: { lg: "sticky" },
                top: { lg: 88 },
                alignSelf: "flex-start",
              }}
            >
              {onAbrirAdelanto ? (
                <Box sx={{ mb: 1.5 }}>
                  <Button
                    variant="outlined"
                    color="primary"
                    fullWidth
                    size="small"
                    startIcon={<RequestQuoteOutlinedIcon />}
                    onClick={onAbrirAdelanto}
                    disabled={loading}
                    sx={{ bgcolor: "#fff", "&:hover": { bgcolor: "#f5f5f5" } }}
                  >
                    Anticipo
                  </Button>
                </Box>
              ) : null}
              <ResumenSeleccion
                preview={preview}
                previewLoading={previewLoading}
                previewError={previewError}
                formatMoneda={formatMoneda}
                cantidadSeleccionada={filasSeleccionadas.length}
                filasSeleccionadas={filasSeleccionadas}
                importesAPagar={importesAPagar}
                cantidadOpsEstimada={(() => {
                  const cuits = new Set(
                    filasSeleccionadas
                      .filter((d) => !esPagoNoProveedor(d))
                      .map((d) => String(d.nro_cuit || "").replace(/\D/g, ""))
                      .filter(Boolean)
                  );
                  const hayPnp = filasSeleccionadas.some(esPagoNoProveedor);
                  if (cuits.size === 0 && hayPnp) return 1;
                  return Math.max(1, cuits.size + (hayPnp ? 0 : 0));
                })()}
                mensajeOpsEstimada={(() => {
                  const cuits = new Set(
                    filasSeleccionadas
                      .filter((d) => !esPagoNoProveedor(d))
                      .map((d) => String(d.nro_cuit || "").replace(/\D/g, ""))
                      .filter(Boolean)
                  );
                  const hayPnp = filasSeleccionadas.some(esPagoNoProveedor);
                  if (cuits.size === 0 && hayPnp) {
                    return "Se generará un lote incluyendo los pagos varios seleccionados.";
                  }
                  if (cuits.size > 1 || (cuits.size >= 1 && hayPnp)) {
                    return `Se generará un lote (${cuits.size} proveedor${cuits.size === 1 ? "" : "es"}${hayPnp ? " + pagos varios" : ""}).`;
                  }
                  return "";
                })()}
                mostrarConfigPago={mostrarConfigPago}
                proveedoresSeleccionados={proveedoresSeleccionadosConfig}
                configPagoPorProveedor={configPagoPorProveedor}
                onConfigPagoChange={handleConfigPagoChange}
                cuentasBancarias={cuentasBancarias}
                loadingCuentas={loadingCuentas}
              />
              <Box sx={{ mt: 1.5, mb: 1 }}>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  size="medium"
                  startIcon={<ReceiptLongIcon />}
                  disabled={!puedeContinuar}
                  onClick={onContinuar}
                >
                  {labelContinuar}
                </Button>
              </Box>
            </Grid>
          ) : null}
        </Grid>
      )}
    </Box>
  );
};

export default ListadoPendientesEmpresa;
export { cuitProveedor, docEsSeleccionable };
